import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FcAddressListItemDto, FcZipListItemDto } from 'webcommon/shared';

import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { CustomValidators, FcItemListBaseComponent, FcListValidationService, FormGroupExtended, warning } from '../../generic';

@Component({
  providers: [FcListValidationService],
  selector: 'fc-address-list',
  templateUrl: './fc-address-list.component.html',
})
export class FcAddressListComponent extends FcItemListBaseComponent implements OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroupExtended;
  @Input() listItem: FcAddressListItemDto;
  @Input() isFullAddress = true;
  @Input() isSubmitted = false;
  @Input() disableControls: BehaviorSubject<boolean[] | null> = new BehaviorSubject<boolean[] | null>([false, false]);
  @Input() itemContext: FcItemModalContext;
  @Output() areaCode = new EventEmitter<string>();

  private disableControlsSubscription: Subscription;

  public addressOneControl = new FormControl();
  public addressTwoControl = new FormControl();
  public cityControl = new FormControl();
  public stateControl = new FormControl();
  public zipControl = new FormControl();
  public countryControl = new FormControl();
  public countyNameControl = new FormControl();
  public countyCodeControl = new FormControl();
  private isFirstRender = true;
  private valueOfZipChanged = false;
  public isZipFindControlRequired = false;
  public selectedPks: string[] = [];

  public get addressFixMessage(): string {
    if (this.form.warnings != null && this.form.warnings.address != null) {
      const { addressOne, addressTwo, city, state, zip } = this.form.warnings.address.correctAddress;
      return `Use ${addressOne} ${addressTwo} ${city}, ${state} ${zip}`;
    }
    return '';
  }

  constructor(
    injector: Injector,
    private readonly http: HttpClient,
  ) {
    super(injector);
    this.formControlToUpperCase(this.addressOneControl);
    this.formControlToUpperCase(this.addressTwoControl);
    this.formControlToUpperCase(this.cityControl);
    this.formControlToUpperCase(this.stateControl);
    this.formControlToUpperCase(this.zipControl);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, {
        Address: {
          MaxLength: 100,
        },
        City: {
          MaxLength: 35,
        },
        State: {
          MaxLength: 3,
        },
        Zip: {
          MaxLength: 50,
        },
        Country: {
          MaxLength: 25,
        },
        CountyName: {
          MaxLength: 35,
        },
        CountyCode: {
          MaxLength: 10,
        },
      });

      if (this.itemContext.mode === FcItemModalMode.New) {
        this.isFirstRender = false;
      }
    }

    if (changes.form != null) {
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
    if (changes.disableControls != null) {
      this.disableControlsSubscription?.unsubscribe();
      this.disableControlsSubscription = this.disableControls.pipe(this.takeUntilDestroy()).subscribe((value: boolean[]) => {
        if (value[0]) {
          this.form.disable();
        } else {
          this.form.enable();
        }
        if (value[1]) {
          this.form.reset();
          this.selectedPks = [];
        }
      });
    }
  }

  private addControlsToForm(): void {
    this.form.addControl('addressOne', this.addressOneControl);
    this.form.addControl('addressTwo', this.addressTwoControl);
    this.form.addControl('city', this.cityControl);
    this.form.addControl('state', this.stateControl);
    this.form.addControl('zip', this.zipControl);
    this.form.addControl('country', this.countryControl);
    this.form.addControl('countyName', this.countyNameControl);
    this.form.addControl('countyCode', this.countyCodeControl);
  }

  private setForm(): void {
    this.form.setAsyncValidators(warning(CustomValidators.address(this.http)));

    // Address One Control
    this.addressOneControl.setValidators(this.fcListValidation.getValidators('Address1'));
    this.addressOneControl.setValue(this.listItem?.Address1 ?? '');

    // Address Two Control
    this.addressTwoControl.setValidators(this.fcListValidation.getValidators('Address2'));
    this.addressTwoControl.setValue(this.listItem?.Address2 ?? '');

    // City Control
    this.cityControl.setValidators(this.fcListValidation.getValidators('City'));
    this.cityControl.setValue(this.listItem?.City ?? '');

    // State Control
    this.stateControl.setValidators(this.fcListValidation.getValidators('State'));
    this.stateControl.setValue(this.listItem?.State ?? '');

    // ZIP Control
    this.zipControl.setValidators(this.fcListValidation.getValidators('Zip'));
    this.zipControl.setValue(this.listItem?.Zip ?? '');
    this.isZipFindControlRequired = this.fcListValidation.validations?.Zip?.IsRequired ?? false;

    // Country Control
    this.countryControl.setValidators(this.fcListValidation.getValidators('Country'));
    this.countryControl.setValue(this.listItem?.Country ?? '');

    // County Name Control
    this.countyNameControl.setValidators(this.fcListValidation.getValidators('CountyName'));
    this.countyNameControl.setValue(this.listItem?.CountyName ?? '');

    // County Code Control
    this.countyCodeControl.setValidators(this.fcListValidation.getValidators('CountyCode'));
    this.countyCodeControl.setValue(this.listItem?.CountyCode ?? '');

    this.populateZipData(this.listItem?.ZipUid ?? '');
  }

  private populateZipData(zipUid: string | null = null): void {
    if (typeof zipUid === 'string' && zipUid.length > 0) {
      this.selectedPks = [zipUid];
    } else {
      this.selectedPks = [];
    }
  }

  public onZipValueChanged(event: boolean) {
    this.valueOfZipChanged = event;
  }

  public onZipDataPopulate(zipArray: FcZipListItemDto[]): void {
    if (!this.isFirstRender) {
      const zipData = { ...zipArray[0] };
      if (zipData.Name) {
        this.zipControl.setValue(zipData.Name);
      } else {
        this.zipControl.setValue('');
      }
      if (this.valueOfZipChanged) {
        this.setCorrectValues(zipData);
      }
    }
    this.isFirstRender = false;
  }

  public setCorrectValues(zipData: FcZipListItemDto): void {
    this.areaCode.emit(zipData.AreaCode);
    if (!this.cityControl.value && !this.stateControl.value) {
      this.cityControl.setValue(zipData.City);
      this.stateControl.setValue(zipData.State);
    }
  }

  public fixAddress(event: Event): void {
    event.preventDefault();
    if (this.form.warnings != null && this.form.warnings.address != null) {
      const { addressOne, addressTwo, city, state, zip } = this.form.warnings.address.correctAddress;
      this.addressOneControl.setValue(addressOne);
      this.addressTwoControl.setValue(addressTwo);
      this.cityControl.setValue(city);
      this.stateControl.setValue(state);
      this.zipControl.setValue(zip);
      this.form.markAsDirty();
    }
  }

  private formControlToUpperCase(formControl: FormControl): void {
    formControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe((value) => {
      if (typeof value === 'string') {
        formControl.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });
  }
}
